import { connect } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type {
  SecondaryPriceType,
  SecondaryQuotationType,
} from 'state/secondaryMarket/productSearch/productSearch.model';
import {
  getSecondaryQuoteAccruedCoupon,
  getSecondaryQuoteCashAmount,
  getSecondaryQuoteDeductedFee,
  getSecondaryQuotePrice,
  getSecondaryQuotePriceType,
  getSecondaryQuoteQuotationType,
  getSecondaryQuoteSettlementDate,
  getSecondaryQuoteSettlementPrice,
  getSecondaryQuoteTradeAuthorizationDurationInMilliseconds,
  getSecondaryQuoteTradeAuthorizationStartTimestampMilliseconds,
  getSecondaryQuoteTradeDate,
  getSecondaryQuoteUpfrontFee,
} from 'state/secondaryMarket/realtimeQuote/quote/quote.selectors';
import { secondaryMarketQuoteCancelledThunk } from 'state/secondaryMarket/realtimeQuote/quote/quote.thunks';
import { getSecondaryMarketRtFormQuoteId } from 'state/secondaryMarket/realtimeQuote/realtimeQuoteForm/realTimeQuoteForm.selectors';
import { secondaryMarketTradeRequestThunk } from 'state/secondaryMarket/realtimeQuote/trade';
import { getSecondaryMarketRealtimeAstroProductFromTab } from 'state/secondaryMarket/referential/astroProduct/astroProduct.selectors';
import type { TradeWay } from 'state/secondaryMarket/secondaryMarket.model';
import { fromSecondaryMarket } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { isSecondaryMarketTradeEnabledForUser } from 'state/user';

type OwnProps = Readonly<{
  tabId: string;
}>;

type StateProps = Readonly<{
  canTrade: boolean;
  isin: string;
  quantity: number;
  quotationType: SecondaryQuotationType;
  tradeWay: TradeWay;
  quoteId: string;
  cashAmount: number | undefined;
  settlementDate: string | undefined;
  accruedCoupon: number | undefined;
  tradeDate: string | undefined;
  upfrontFee: number | undefined;
  deductedFee: string | undefined;
  settlementPrice: number;
  price: number;
  priceType: SecondaryPriceType;
  currency: string;
  tradeAuthDurationInMillis: number | undefined;
  tradeAuthStartTimeInMillis: number | undefined;
}>;

type DispatchProps = Readonly<{
  onCancelTrade: () => void;
  onTrade: () => void;
}>;

export type QuotedSidePanelProps = OwnProps & StateProps & DispatchProps;

const mapStateToProps = (state: AppState, { tabId }: OwnProps): StateProps => {
  const isin = getSecondaryMarketRealtimeAstroProductFromTab(state, tabId)?.data?.isin;
  const quantity = fromSecondaryMarket.realtimeQuote.form.getSecondaryMarketRtFormQuantity(state, tabId)!;
  const tradeSide = fromSecondaryMarket.realtimeQuote.form.getSecondaryMarketRtFormTradeWay(state, tabId);
  const quoteId = getSecondaryMarketRtFormQuoteId(state, tabId) as string;
  const cashAmount = getSecondaryQuoteCashAmount(state, quoteId);
  const settlementDate = getSecondaryQuoteSettlementDate(state, quoteId);
  const accruedCoupon = getSecondaryQuoteAccruedCoupon(state, quoteId);
  const tradeDate = getSecondaryQuoteTradeDate(state, quoteId);
  const upfrontFee = getSecondaryQuoteUpfrontFee(state, quoteId);
  const deductedFee = getSecondaryQuoteDeductedFee(state, quoteId);
  const settlementPrice = getSecondaryQuoteSettlementPrice(state, quoteId)!;
  const priceType = getSecondaryQuotePriceType(state, quoteId)!;
  const quotationType = getSecondaryQuoteQuotationType(state, quoteId)!;
  const tradeAuthStartTimeInMillis = getSecondaryQuoteTradeAuthorizationStartTimestampMilliseconds(state, quoteId);
  const tradeAuthDurationInMillis = getSecondaryQuoteTradeAuthorizationDurationInMilliseconds(state, quoteId);
  const product = getSecondaryMarketRealtimeAstroProductFromTab(state, tabId);
  const price = getSecondaryQuotePrice(state, quoteId)!;
  const canTrade = isSecondaryMarketTradeEnabledForUser(state);

  return {
    canTrade,
    quoteId,
    quantity,
    currency: product?.data?.currency!,
    isin: isin!,
    quotationType,
    tradeWay: tradeSide!,
    cashAmount,
    settlementDate,
    accruedCoupon,
    tradeDate,
    upfrontFee,
    deductedFee,
    settlementPrice,
    priceType,
    tradeAuthStartTimeInMillis,
    tradeAuthDurationInMillis,
    price,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch, { tabId }: OwnProps) => {
  return {
    onCancelTrade: () => {
      dispatch(secondaryMarketQuoteCancelledThunk(tabId));
      logAnalytics.trackEvent('Secondary market', 'Cancel quote');
    },
    onTrade: () => {
      dispatch(secondaryMarketTradeRequestThunk(tabId));
      logAnalytics.trackEvent('Secondary market', 'Request for trade');
    },
  };
};

export const connectQuotedSidePanel = connect(mapStateToProps, mapDispatchToProps);
